import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { Grid } from 'lucide-react'

const Login: React.FC = () => {
  const { login, isLoading, isAuthenticated } = useAuth()
  const [localError, setLocalError] = useState<string | null>(null)

  // If already authenticated (e.g. after MSAL redirect completes), bounce to home
  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const handleLogin = async () => {
    try {
      setLocalError(null)
      await login()
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Sign in failed. Please try again.')
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--color-white)',
        fontFamily: 'var(--font-family)',
        padding: '20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          borderRadius: '8px',
          padding: '48px 32px',
          backgroundColor: 'var(--color-white)',
        }}
      >
        {/* Logo */}
        <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'center' }}>
          <img
            src="/logo-landscape.svg"
            alt="The Lab"
            style={{ height: '64px', width: 'auto', display: 'block' }}
          />
        </div>

        {/* Subtitle */}
        <p
          style={{
            fontSize: '14px',
            color: 'var(--color-dark-grey)',
            marginBottom: '32px',
            lineHeight: '1.5',
          }}
        >
          Sign in to access The Project Lab's internal platform.
        </p>

        {/* Error message */}
        {localError && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '13px',
              marginBottom: '20px',
              border: '1px solid #fecaca',
            }}
          >
            {localError}
          </div>
        )}

        {/* Sign in button */}
        <button
          onClick={handleLogin}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '14px 16px',
            backgroundColor: isLoading ? 'var(--color-light-grey)' : 'var(--color-magenta)',
            color: 'var(--color-white)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            transition: 'background-color 0.2s',
            fontFamily: 'var(--font-family)',
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.currentTarget.style.backgroundColor = 'var(--color-dark-magenta)'
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.currentTarget.style.backgroundColor = 'var(--color-magenta)'
            }
          }}
        >
          {isLoading ? (
            <>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid var(--color-white)',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite',
                }}
              />
              Signing in...
            </>
          ) : (
            <>
              <Grid size={20} />
              Sign in with Microsoft
            </>
          )}
        </button>

        {/* Footer */}
        <p
          style={{
            fontSize: '12px',
            color: 'var(--color-dark-grey)',
            marginTop: '32px',
            margin: '32px 0 0 0',
          }}
        >
          Powered by The Project Lab
        </p>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  )
}

export default Login
